import { useCallback, useEffect, useRef, useState } from 'react'
import {
  TERMINAL_EXECUTION_STATUSES,
  type Execution,
  type ExecutionDetail,
  type NodeRun,
} from '../api/types.js'
import type { GraphNode } from './graph.js'
import type { ItemView } from './DataPanel.js'
import { formatMoment } from '../ui/moment.js'

export interface RunLogClient {
  execution(id: string): Promise<ExecutionDetail>
  retryFrom(executionId: string, nodeRunId: string): Promise<ExecutionDetail>
}

export interface RunLogProps {
  client: RunLogClient
  executionId: string
  /** Узлы графа: шаг называется именем ноды, а не её идентификатором. */
  nodes?: readonly GraphNode[]
  /** Права обратившегося: повтор доступен только с `executions.write`. */
  permissions?: readonly string[]
  /** Новый прогон, начатый повтором: экран открывает его вместо текущего. */
  onReplayed?: (executionId: string) => void
  /**
   * Прочитанный прогон — тому, кто показывает его не журналом (FR-ADM-034).
   * Холст красит узлы по исходам их шагов, а читает прогон журнал: свой
   * второй запрос за теми же данными означал бы и вторую линию опроса, и
   * два разных представления одного прогона на одном экране.
   */
  onExecution?: (execution: ExecutionDetail) => void
  /** Период опроса незакончившегося прогона. Настраивается ради проверяемости. */
  pollDelayMs?: number
}

export const RETRY_PERMISSION = 'executions.write'
export const DEFAULT_RUN_POLL_DELAY_MS = 1000

const STEP_STATUS_NAMES: Readonly<Record<string, string>> = {
  running: 'идёт',
  success: 'успешно',
  failed: 'с ошибкой',
  waiting: 'ждёт',
  skipped: 'пропущен',
  cancelled: 'отменён',
}

function statusNameOf(status: string): string {
  return STEP_STATUS_NAMES[status] ?? status
}

function nodeNameOf(nodes: readonly GraphNode[], nodeId: string): string {
  return nodes.find((node) => node.id === nodeId)?.name ?? nodeId
}

/**
 * Данные шага человеку. Отступы обязательны: строка в одну линию длиной в
 * экран — это не то, в чём ищут поле.
 */
function asJson(value: unknown): string {
  if (value === undefined || value === null) return '—'
  return JSON.stringify(value, null, 2) ?? '—'
}

/**
 * Журнал прогона (FR-UI-005). Шаг раскрывается по нажатию: показывать вход
 * и выход всех шагов сразу значит страницу, по которой нельзя прокрутиться
 * к нужному.
 *
 * Повтор идёт с шага по его сохранённому входу (FR-PIPE-020, FR-UI-006):
 * иначе после исправления ноды пришлось бы прогонять сценарий с начала — и
 * заново отправлять всё, что он успел отправить.
 */
export function RunLog({
  client,
  executionId,
  nodes = [],
  permissions = [],
  onReplayed,
  onExecution,
  pollDelayMs = DEFAULT_RUN_POLL_DELAY_MS,
}: RunLogProps): React.JSX.Element {
  const [execution, setExecution] = useState<ExecutionDetail | null>(null)
  const [openStepId, setOpenStepId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const alive = useRef(true)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const current = useRef(client)
  current.current = client
  // Через ref, а не зависимостью эффекта: обработчик, заново созданный
  // родителем на каждую отрисовку, перезапускал бы опрос с начала.
  const сообщить = useRef(onExecution)
  сообщить.current = onExecution

  useEffect(
    () => () => {
      alive.current = false
      if (timer.current !== null) clearTimeout(timer.current)
    },
    [],
  )

  useEffect(() => {
    let cancelled = false

    const load = (): void => {
      current.current
        .execution(executionId)
        .then((detail) => {
          if (cancelled || !alive.current) return
          setExecution(detail)
          setError(null)
          сообщить.current?.(detail)
          // Незакончившийся прогон дочитывается: журнал, замерший на
          // «идёт», ничего не говорит о том, чем всё кончилось.
          if (!TERMINAL_EXECUTION_STATUSES.includes(detail.status)) {
            timer.current = setTimeout(load, pollDelayMs)
          }
        })
        .catch((failure: unknown) => {
          if (cancelled || !alive.current) return
          setError(failure instanceof Error ? failure.message : 'Не удалось прочитать прогон.')
        })
    }

    load()

    return () => {
      cancelled = true
      if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [executionId, pollDelayMs])

  const replay = useCallback(
    (nodeRunId: string): void => {
      setRetrying(true)
      setError(null)
      current.current
        .retryFrom(executionId, nodeRunId)
        .then((replayed) => {
          if (!alive.current) return
          setRetrying(false)
          onReplayed?.(replayed.id)
        })
        .catch((failure: unknown) => {
          if (!alive.current) return
          setRetrying(false)
          setError(failure instanceof Error ? failure.message : 'Повтор не запустился.')
        })
    },
    [executionId, onReplayed],
  )

  const canRetry = permissions.includes(RETRY_PERMISSION)

  return (
    <section className="журнал" aria-label="Журнал прогона">
      {error !== null ? (
        <p className="журнал__отказ" role="alert">
          {error}
        </p>
      ) : null}

      {execution === null ? (
        <p className="журнал__загрузка">Читаем прогон…</p>
      ) : (
        <>
          <p className="журнал__итог">{`Прогон ${statusNameOf(execution.status)}`}</p>
          {execution.error !== null ? <p className="журнал__ошибка">{execution.error}</p> : null}
          {execution.replayOf !== null ? (
            // Иначе повтор выглядит как обычный прогон, и непонятно, почему
            // в нём нет первых шагов.
            <p className="журнал__повтор">{`Это повтор прогона ${execution.replayOf.executionId} с шага.`}</p>
          ) : null}

          {execution.nodeRuns.length === 0 ? (
            <p className="журнал__пусто">Ни один шаг не начался: прогон оборвался раньше, чем дошёл до первого узла.</p>
          ) : (
            <ol className="журнал__шаги">
              {execution.nodeRuns.map((step) => (
                <li key={step.id}>
                  <button
                    type="button"
                    aria-expanded={openStepId === step.id}
                    onClick={() => {
                      setOpenStepId(openStepId === step.id ? null : step.id)
                    }}
                  >
                    {nodeNameOf(nodes, step.nodeId)}
                  </button>
                  <span className="шаг__состояние">{statusNameOf(step.status)}</span>
                  {step.attempt > 1 ? (
                    // Число попыток объясняет, почему шаг занял столько
                    // времени: он повторялся по политике узла.
                    <span className="шаг__попытки">{`Попыток: ${step.attempt}`}</span>
                  ) : null}

                  {openStepId === step.id ? (
                    <div className="шаг__подробности">
                      {step.error !== null ? (
                        <p className="шаг__ошибка">{step.error}</p>
                      ) : null}
                      {/* Вход и выход — рядом, а не друг под другом: шаг
                          разбирают, сравнивая одно с другим, и колонкой они
                          не помещались в кадр никогда. */}
                      <div className="шаг__данные">
                        <div className="шаг__данные-часть">
                          <h4>Вход</h4>
                          <pre>{asJson(step.input)}</pre>
                        </div>
                        <div className="шаг__данные-часть">
                          <h4>Выход</h4>
                          <pre>{asJson(step.output)}</pre>
                        </div>
                      </div>

                      {canRetry ? (
                        <button
                          type="button"
                          disabled={retrying}
                          onClick={() => {
                            replay(step.id)
                          }}
                        >
                          Повторить с этого шага
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  )
}

export interface RunListClient {
  executions(
    pipelineId: string,
    page?: { limit?: number; cursor?: string; status?: string },
  ): Promise<{ items: Execution[]; nextCursor: string | null }>
}

export interface RunListProps {
  client: RunListClient
  pipelineId: string
  onOpen?: (executionId: string) => void
}

/**
 * Цвет состояния прогона (FR-ADM-040). Тот же набор, что у номеров и у
 * узлов холста: зелёный работает, синий идёт, жёлтый остановлен, красный
 * сломан. Своей палитры у списка нет намеренно — иначе один и тот же
 * зелёный пришлось бы читать заново в каждом разделе.
 */
const ТОН_ПРОГОНА: Readonly<Record<string, string>> = {
  running: 'progress',
  waiting: 'warn',
  cancelling: 'progress',
  success: 'ok',
  failed: 'danger',
  cancelled: 'warn',
}

export function статусныйТон(status: string): string {
  return ТОН_ПРОГОНА[status] ?? 'neutral'
}

/**
 * Сколько прогон шёл. `null` у незакончившегося: писать «идёт 0 с» значит
 * показывать число, которое устарело в тот же миг.
 *
 * Секунды и минуты, а не миллисекунды: разбирают прогоны по порядку
 * величины — «полминуты вместо секунды», — и три знака после запятой этому
 * только мешают.
 */
export function длительность(execution: Execution): string | null {
  if (execution.stoppedAt === null) return null
  const начало = new Date(execution.startedAt).getTime()
  const конец = new Date(execution.stoppedAt).getTime()
  if (Number.isNaN(начало) || Number.isNaN(конец) || конец < начало) return null

  const секунд = (конец - начало) / 1000
  if (секунд < 1) return `${Math.round(секунд * 1000)} мс`
  if (секунд < 60) return `${секунд < 10 ? секунд.toFixed(1) : Math.round(секунд)} с`
  return `${Math.floor(секунд / 60)} мин ${Math.round(секунд % 60)} с`
}

/** Отбор по состоянию: чаще всего ищут именно упавшие прогоны. */
const STATUS_FILTERS: readonly { value: string; label: string }[] = [
  { value: '', label: 'Любое' },
  { value: 'running', label: 'Идут' },
  { value: 'waiting', label: 'Ждут' },
  { value: 'success', label: 'Успешные' },
  { value: 'failed', label: 'С ошибкой' },
  { value: 'cancelled', label: 'Отменённые' },
]

/**
 * Список прогонов сценария (FR-UI-005). Отбор идёт запросом к службе, а не
 * фильтром по загруженной странице: упавший прогон может быть сотым, и
 * фильтровать первые пятьдесят значит его не найти.
 */
export function RunList({ client, pipelineId, onOpen }: RunListProps): React.JSX.Element {
  const [status, setStatus] = useState('')
  const [items, setItems] = useState<readonly Execution[]>([])
  const [error, setError] = useState<string | null>(null)
  const current = useRef(client)
  current.current = client

  useEffect(() => {
    let cancelled = false
    current.current
      .executions(pipelineId, status === '' ? {} : { status })
      .then((page) => {
        if (cancelled) return
        setItems(page.items)
        setError(null)
      })
      .catch((failure: unknown) => {
        if (cancelled) return
        setError(failure instanceof Error ? failure.message : 'Не удалось прочитать прогоны.')
      })

    return () => {
      cancelled = true
    }
  }, [pipelineId, status])

  return (
    <section className="прогоны" aria-label="Прогоны сценария">
      <label htmlFor="прогоны-статус">Статус</label>
      <select
        id="прогоны-статус"
        value={status}
        onChange={(event) => {
          setStatus(event.target.value)
        }}
      >
        {STATUS_FILTERS.map((filter) => (
          <option key={filter.value} value={filter.value}>
            {filter.label}
          </option>
        ))}
      </select>

      {error !== null ? (
        <p className="прогоны__отказ" role="alert">
          {error}
        </p>
      ) : null}

      {items.length === 0 && error === null ? (
        <p className="прогоны__пусто">Прогонов пока нет. Они появятся здесь после тестового запуска или первого срабатывания триггера.</p>
      ) : (
        // Состояние — цветным ярлыком, тем же, что у номеров в списке
        // (FR-ADM-040). Список прогонов читают затем, чтобы найти упавший,
        // и именно его в одинаково набранных строках было не видно.
        // Длительность рядом: прогон, который шёл минуту вместо секунды,
        // разбирают по той же причине, что и упавший.
        <ul>
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => {
                  onOpen?.(item.id)
                }}
              >
                {formatMoment(item.startedAt)}
              </button>
              <span className={`badge badge--${статусныйТон(item.status)}`}>
                {statusNameOf(item.status)}
              </span>
              {длительность(item) !== null ? (
                <span className="прогоны__длительность">{длительность(item)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Шаг прогона по узлу: панели данных нужен вход именно этого узла. */
export function stepsByNode(nodeRuns: readonly NodeRun[]): Record<string, NodeRun[]> {
  const byNode: Record<string, NodeRun[]> = {}
  for (const step of nodeRuns) {
    byNode[step.nodeId] = [...(byNode[step.nodeId] ?? []), step]
  }
  return byNode
}

/**
 * Данные шага как элементы. Служба хранит их ветвями (`Item[][]`, по одной
 * на выход), а панели данных и подсказкам нужны сами элементы: делить их по
 * ветвям здесь незачем — поля у элементов одни и те же.
 */
export function itemsOf(payload: unknown): ItemView[] {
  if (!Array.isArray(payload)) return []
  const items: ItemView[] = []
  for (const entry of payload) {
    if (Array.isArray(entry)) items.push(...itemsOf(entry))
    else if (isItem(entry)) items.push(entry)
  }
  return items
}

function isItem(value: unknown): value is ItemView {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, 'json') &&
    typeof (value as { json: unknown }).json === 'object' &&
    (value as { json: unknown }).json !== null
  )
}

/**
 * Вход каждого узла в прогоне — панели данных задачи 18. Берётся вход, а не
 * выход: параметры узла пишутся по тому, что в него пришло.
 *
 * Из нескольких срабатываний узла (цикл) берётся последнее: оно и осталось
 * бы на экране, а показывать первое значит показывать данные, которых на
 * момент отказа уже не было.
 */
export function itemsByNodeFrom(nodeRuns: readonly NodeRun[]): Record<string, ItemView[]> {
  const byNode: Record<string, ItemView[]> = {}
  for (const [nodeId, steps] of Object.entries(stepsByNode(nodeRuns))) {
    const last = steps[steps.length - 1]
    if (last !== undefined) byNode[nodeId] = itemsOf(last.input)
  }
  return byNode
}

/** Чем кончился шаг узла и сколько элементов он отдал (FR-ADM-034). */
export interface NodeOutcome {
  status: string
  items: number
}

/**
 * Итоги прогона по узлам — холсту (FR-ADM-034).
 *
 * Исход берётся у последнего срабатывания узла: им прогон и кончился, а
 * показывать первое значит показывать то, что уже неверно. Элементы, в
 * отличие от исхода, складываются по всем срабатываниям: в цикле узел
 * отрабатывает партию за партией, и «обработано 20» — это весь цикл, а не
 * последняя его партия. Неудачная попытка ничего не отдаёт и в сумму
 * не попадает сама собой.
 *
 * Узла, который в прогоне не участвовал, здесь нет вовсе — и это ответ, а
 * не пробел: невыбранная ветвь «Условия» обязана остаться на холсте
 * нетронутой.
 */
export function итогиУзловИз(nodeRuns: readonly NodeRun[]): Record<string, NodeOutcome> {
  const byNode: Record<string, NodeOutcome> = {}
  for (const [nodeId, steps] of Object.entries(stepsByNode(nodeRuns))) {
    const last = steps[steps.length - 1]
    if (last === undefined) continue
    byNode[nodeId] = {
      status: last.status,
      items: steps.reduce((sum, step) => sum + itemsOf(step.output).length, 0),
    }
  }
  return byNode
}
